import { Box, Typography } from '@mui/material'
import {
  getGridStringOperators,
  GridAggregationFunction,
  GridColDef,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowId,
  GridTreeNodeWithRender,
  GridValueFormatter,
} from '@mui/x-data-grid-premium'
import ChildDateCell from 'components/DataGrid/ChildRows/ChildDateCell'
import 'components/DataGrid/DataGridContext.css'
import { seoStatusOptions, taskMasterStatusOptions } from 'components/DataGrid/filterArrays'
import { customMonthFilterOperators } from 'components/DataGrid/operators/customMonthFilterOperators'
import { customMonthRangeFilterOperators } from 'components/DataGrid/operators/customMonthRangeFilter'
import { customNotContainsOperator } from 'components/DataGrid/operators/customNotContainsOperator'
import { customNumericSortOperator, customSortOperator } from 'components/DataGrid/operators/customSortOperator'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import { customNotAnyOfOperator } from 'components/DataGrid/operators/isAnyOfOperator'
import RenderOptimizationsColumn from 'components/MTB/RenderOptimizationsColumn'
import EditSelectCell from 'components/NCO/EditSelectCell'
import TeamMemberCell from 'components/Organizations/grid/cells/TeamMemberCell'
import dayjs, { Dayjs } from 'dayjs'
import { FlexRow } from 'theme/components'
import { DensityStyleTypes, Task, User } from 'types'
import { formatDateString, formatDayjsDateTime, formatDayjsModifiedTime } from 'utils/formatDayjsDateTime'
import { formatDate } from 'utils/formatters/formatDates'
import ActiveCountCell from './cells/ActiveCountCell'
import AggregateCurrencyCell from './cells/AggregateCell'
import CommentCell from './cells/CommentCell'
import CommentCellHeader from './cells/CommentCellHeader'
import ContractedByCell from './cells/ContractedByCell'
import DateCell from './cells/DateCell'
import DmsOptimizationCell from './cells/DmsOptimizationCell'
import InactiveCell from './cells/InactiveCell'
import MasterStatusCell from './cells/MasterStatusCell'
import MtbOrganizationCell from './cells/MtbOrganizationCell'
import NumberCell from './cells/NumberCell'
import ProgressCell from './cells/ProgressCell'
import SeoOptimizationCell from './cells/SeoOptimizationsCell'
import SeoStatusCell from './cells/SeoStatusCell'
import SeoTierCell from './cells/SeoTierCell'
import TypeCell from './cells/TypeCell'
import EditDatePicker from './EditDatePicker'

const currencyValueFormatter: GridValueFormatter = ({ value }) =>
  value == null
    ? ''
    : (value as number).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })

export const safeSum: GridAggregationFunction<number, number> = {
  label: 'Safe sum',
  apply: ({ values }) => values.reduce((sum, v) => (typeof v === 'number' && !Number.isNaN(v) ? sum + v : sum), 0),
  valueFormatter: currencyValueFormatter,
}
export const renderMtbColumns = (
  density: 'compact' | 'standard' | 'comfortable',
  handleRowClick: (row: GridRenderCellParams['row'], pathPrefix?: string) => void,
  toggleExpanded: (id: GridRowId) => void,
  handleClickEditStart: (params: GridRenderCellParams) => void
): GridColDef[] => {
  const densityStyles: DensityStyleTypes = getDensityStyles(density)

  return [
    {
      field: 'name',
      aggregable: false,
      headerName: 'Organization Name',
      width: 350,
      // flex: 1,
      valueFormatter: (_, row) => {
        const org = row ?? null
        const value = row?.isParent ? row?.name : `${org?.name ?? ''}-${row?.name ?? ''}`
        return value
      },
      valueGetter: (_, row) => row?.name,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <MtbOrganizationCell
            params={params}
            densityStyles={densityStyles}
            handleRowClick={handleRowClick}
            toggleExpanded={toggleExpanded}
          />
        )
      },
    },
    {
      field: 'orgNameReference',
      aggregable: false,
      headerName: 'Related Org',
      headerClassName: 'related-org-column',
      // hide: true,
      filterable: true,
      // valueGetter: (_, row) => {
      //   return row?.orgNameReference ?? ''
      // },
    },
    {
      field: 'commentCount',
      aggregable: false,
      pinnable: false,
      width: 100,
      // flex: 0.3,
      headerName: 'Comments',
      disableColumnMenu: true,
      renderHeader: () => <CommentCellHeader densityStyles={densityStyles} />,
      align: 'center',
      valueGetter: (_, row) => {
        const count = row?.commentCount ?? 0
        return count
      },
      renderCell: (params: GridRenderCellParams) => {
        return (
          <CommentCell
            params={params}
            densityStyles={densityStyles}
            route={`/mtb/${params?.row?.PK}/comments`}
            handleRowClick={handleRowClick}
          />
        )
      },
    },
    {
      field: 'activeCount',
      aggregable: false,
      type: 'number',
      headerName: 'Active Deals',
      width: 100,
      // flex: 0.4,
      valueFormatter: (value: { total: number }) => {
        return value?.total ?? 0 // ✅ Only return primitive here
      },
      valueGetter: (_, row) => {
        return row?.isParent ? row?.activeCount?.total : 0 // ✅ Only return primitive here
      },
      renderCell: params => {
        return params?.row?.isParent ? (
          <ActiveCountCell params={params} densityStyles={densityStyles} />
        ) : (
          <FlexRow extraStyles={{ justifyContent: 'center', color: 'text.disabled' }}>—</FlexRow>
        )
      },
    },

    // {
    //   field: 'billingContact',
    //   headerName: 'Contact',
    //   width: 100,
    //   flex: 0.4,
    //   valueGetter: (_, row) => {
    //     const billingContact = searchContacts(row?.billingContact)
    //     return billingContact ? billingContact?.name : null
    //   },
    //   renderCell: (params: GridRenderCellParams) => {
    //     return <ContactCell id={params?.row?.billingContact} params={params} />
    //   },
    // },
    {
      field: 'searchSpend',
      aggregable: false, // Using pre-computed totals, no aggregation needed
      type: 'number',
      headerName: 'Search Spend',
      width: 100,
      // flex: 0.4,
      valueFormatter: params => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(params)
      },
      valueGetter: (_, row): number => {
        // For parent rows, use pre-computed totals; for product rows, use product's mediaSpend
        if (row?.isParent) return row?.searchSpend ?? 0
        return row?.productName === 'Paid Search Advertising' ? parseFloat(row?.mediaSpend) || 0 : 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'metaSpend',
      aggregable: false, // Using pre-computed totals
      type: 'number',
      headerName: 'Meta Spend',
      width: 100,
      valueFormatter: params => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(params)
      },
      valueGetter: (_, row): number => {
        if (row?.isParent) return row?.metaSpend ?? 0
        return row?.productName === 'Meta Advertising' ? parseFloat(row?.mediaSpend) || 0 : 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'bingSpend',
      aggregable: false, // Using pre-computed totals
      type: 'number',
      headerName: 'Bing Spend',
      width: 100,
      valueFormatter: params => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(params)
      },
      valueGetter: (_, row): number => {
        if (row?.isParent) return row?.bingSpend ?? 0
        return row?.productName === 'Microsoft Advertising' ? parseFloat(row?.mediaSpend) || 0 : 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'spotifySpend',
      aggregable: false, // Using pre-computed totals
      type: 'number',
      headerName: 'Spotify Spend',
      width: 100,
      valueFormatter: params => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(params)
      },
      valueGetter: (_, row): number => {
        if (row?.isParent) return row?.spotifySpend ?? 0
        return row?.productName === 'Spotify Advertising' ? parseFloat(row?.mediaSpend) || 0 : 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'linkedinSpend',
      aggregable: false, // Using pre-computed totals
      type: 'number',
      headerName: 'LinkedIn Spend',
      width: 100,
      valueFormatter: params => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(params)
      },
      valueGetter: (_, row): number => {
        if (row?.isParent) return row?.linkedinSpend ?? 0
        return row?.productName === 'Linkedin Advertising' ? parseFloat(row?.mediaSpend) || 0 : 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'snapchatSpend',
      aggregable: false, // Using pre-computed totals
      type: 'number',
      headerName: 'Snapchat Spend',
      width: 100,
      valueFormatter: params => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(params)
      },
      valueGetter: (_, row): number => {
        if (row?.isParent) return row?.snapchatSpend ?? 0
        return row?.productName === 'Snapchat Advertising' ? parseFloat(row?.mediaSpend) || 0 : 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'tiktokSpend',
      aggregable: false, // Using pre-computed totals
      type: 'number',
      headerName: 'TikTok Spend',
      width: 100,
      valueFormatter: params => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(params)
      },
      valueGetter: (_, row): number => {
        if (row?.isParent) return row?.tiktokSpend ?? 0
        return row?.productName === 'TikTok Advertising' ? parseFloat(row?.mediaSpend) || 0 : 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'progDisplaySpend',
      aggregable: false, // Using pre-computed totals
      type: 'number',
      headerName: 'P. Video Spend',
      width: 100,
      valueFormatter: params => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(params)
      },
      valueGetter: (_, row): number => {
        if (row?.isParent) return row?.progDisplaySpend ?? 0
        return row?.productName === 'Programmatic Display Advertising' ? parseFloat(row?.mediaSpend) || 0 : 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'progAudioSpend',
      aggregable: false, // Using pre-computed totals
      type: 'number',
      headerName: 'Audio Spend',
      width: 100,
      valueFormatter: params => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(params)
      },
      valueGetter: (_, row): number => {
        if (row?.isParent) return row?.progAudioSpend ?? 0
        return row?.productName === 'Programmatic Audio Advertising' ? parseFloat(row?.mediaSpend) || 0 : 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'displaySpend',
      aggregable: false, // Using pre-computed totals
      type: 'number',
      headerName: 'Display Spend',
      width: 100,
      valueFormatter: params => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(params)
      },
      valueGetter: (_, row): number => {
        if (row?.isParent) return row?.displaySpend ?? 0
        return row?.productName === 'Google Display Advertising' ? parseFloat(row?.mediaSpend) || 0 : 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'overallProgress',
      aggregable: false,
      type: 'number',
      headerName: 'Progress',
      width: 100,
      // flex: 0.3,
      valueGetter: (_, row) => {
        const progress = row?.overallProgress
        return typeof progress === 'number' ? Math.round(progress * 100) : 0
      },
      renderCell: (params: GridRenderCellParams) => {
        return <ProgressCell params={params} densityStyles={densityStyles} />
      },
    },
    {
      field: 'amProgress',
      aggregable: false,
      pinnable: false,
      headerName: 'AM Progress',
      width: 100,
      // flex: 0.3,
      valueGetter: (_, row) => {
        if (row?.isParent) {
          const progress = row?.amProgress
          return typeof progress === 'number' ? Math.round(progress * 100) : 0
        }
        // For child rows, return start date for display
        return !row?.isParent && row?.startDate ? formatDate(row?.startDate) : null
      },
      renderCell: params => {
        //@ts-ignore
        if (params?.rowNode?.isAutoGenerated) return
        return params?.row?.isParent ? (
          <ProgressCell params={params} densityStyles={densityStyles} />
        ) : (
          <ChildDateCell params={params} densityStyles={densityStyles} label="Start Date" />
        )
      },
    },
    {
      field: 'seoProgress',
      aggregable: false,
      pinnable: false,
      headerName: 'SEO Progress',
      width: 100,
      // flex: 0.3,
      valueGetter: (_, row) => {
        if (row?.isParent) {
          const progress = row?.seoProgress
          return typeof progress === 'number' ? Math.round(progress * 100) : 0
        }
        // For child rows, return end date for display
        return !row?.isParent && row?.endDate ? formatDate(row?.endDate) : null
      },
      renderCell: (params: GridRenderCellParams) => {
        //@ts-ignore
        if (params?.rowNode?.isAutoGenerated) return
        return params?.row?.isParent ? (
          <ProgressCell params={params} densityStyles={densityStyles} />
        ) : (
          <ChildDateCell params={params} densityStyles={densityStyles} label="End Date" />
        )
      },
    },
    {
      field: 'dmsProgress',
      aggregable: false,
      headerName: 'DMS Progress',
      width: 100,
      // flex: 0.3,
      valueGetter: (_, row) => {
        const progress = row?.dmsProgress
        return typeof progress === 'number' ? Math.round(progress * 100) : 0
      },
      renderCell: (params: GridRenderCellParams) => {
        return <ProgressCell params={params} densityStyles={densityStyles} />
      },
    },
    {
      field: 'dmsOpts',
      aggregable: true,
      type: 'number',
      headerName: 'DMS Opts',
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row) => {
        return row?.dmsOpts ?? 0
      },
      renderCell: params => {
        return <DmsOptimizationCell params={params} />
      },
    },
    {
      field: 'seoOpts',
      aggregable: true,
      type: 'number',
      headerName: 'SEO Opts',
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row) => {
        return row?.seoOpts ?? 0
      },
      renderCell: params => {
        return <SeoOptimizationCell params={params} />
      },
    },
    {
      field: 'totalQualifiedOpts',
      aggregable: false,
      headerName: 'Qual. Opts',
      width: 75,
      // flex: 0.4,
      valueGetter: (_, row) => row?.totalQualifiedOpts ?? 0,
      renderCell: params => {
        return <NumberCell params={params} densityStyles={densityStyles} />
      },
    },
    {
      field: 'opts',
      aggregable: true,
      headerName: 'Opts',
      type: 'number',
      width: 55,
      // flex: 0.5,
      headerAlign: 'center',
      sortComparator: customNumericSortOperator,
      valueGetter: (_, row) => {
        return row?.opts ?? 0
      },
      renderCell: params => {
        return <RenderOptimizationsColumn params={params} />
      },
    },
    {
      field: 'onboardingStatus',
      aggregable: false,
      pinnable: false,
      headerName: 'Onboarding Status',
      width: 100,
      // flex: 0.4,
      editable: false,
      valueGetter: (_, row) => {
        return row?.isParent ? row?.onboardingStatus : '–'
      },
      renderCell: params => {
        if (!params?.row?.isParent)
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <Typography sx={{ textAlign: 'center', fontSize: 16 }}>{params?.value}</Typography>
            </Box>
          )
        return <SeoStatusCell params={params} onClick={handleClickEditStart} />
      },
      // renderEditCell: params => {
      //   if (!params?.row?.isParent) return
      //   return <EditSelectCell params={params} options={seoStatusOptions} />
      // },
    },
    {
      field: 'vertical',
      aggregable: false,
      pinnable: false,
      headerName: 'Vertical',
      width: 100,
      // flex: 0.4,
      editable: true,
      valueGetter: params => {
        return params ?? 'Not Started'
      },
      renderCell: params => {
        return <SeoStatusCell params={params} onClick={handleClickEditStart} />
      },
      renderEditCell: params => <EditSelectCell params={params} options={seoStatusOptions} />,
    },
    {
      field: 'masterStatus',
      aggregable: false,
      pinnable: false,
      headerName: 'Status',
      width: 100,
      // flex: 0.4,
      editable: true,
      valueGetter: (_, row) => {
        const status = row?.isParent || row?.isOrgTask ? row?.masterStatus : row?.status ?? {}
        return status ?? 'Not Started'
      },
      renderCell: params => {
        return <MasterStatusCell params={params} onClick={handleClickEditStart} />
      },
      filterOperators: [...getGridStringOperators(), customNotAnyOfOperator],
      renderEditCell: params => <EditSelectCell params={params} options={taskMasterStatusOptions} />,
    },
    // {
    //   field: 'digitalProductsThisMonthTotal',
    //   headerName: 'Digital Total (Mo)',
    //   width: 150,
    //   flex: 0.5,
    //   type: 'number',
    //   valueGetter: (_, row) => {
    //     const month = monthFilter
    //     if (!month || !row?.products) return 0

    //     const [yearStr, monthStr] = month.split('-')
    //     const year = parseInt(yearStr, 10)
    //     const monthIdx = parseInt(monthStr, 10)

    //     const filtered = row.products.filter(product => {
    //       // if (!isSpendableProduct(product?.productName)) return false

    //       const start = product.startDate ? new Date(product.startDate) : null
    //       const end = product.endDate ? new Date(product.endDate) : null
    //       if (!start || !end) return false

    //       const startOfMonth = new Date(year, monthIdx - 1, 1)
    //       const endOfMonth = new Date(year, monthIdx, 0)

    //       return start <= endOfMonth && end >= startOfMonth
    //     })

    //     const total = filtered.reduce((sum, product) => {
    //       const rawPrice = product?.price
    //       const price = typeof rawPrice === 'string' ? parseFloat(rawPrice) : rawPrice
    //       return !isNaN(price) ? sum + price : sum
    //     }, 0)

    //     return total
    //   },
    //   renderCell: params => {
    //     const total = params?.value || 0
    //     return <CurrencyCell params={{ ...params, value: total }} densityStyles={densityStyles} />
    //   },
    // },
    // { field: 'seoGmb', headerName: 'GMB', width: 55, aggregable: false, pinnable: false },
    {
      field: 'type',
      aggregable: false,
      pinnable: false,
      headerName: 'Type',
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row) => row?.type,
      renderCell: (params: GridRenderCellParams<Task, any, any, GridTreeNodeWithRender>) => (
        <TypeCell params={params} />
      ),
    },
    {
      field: 'timeCompleted',
      aggregable: false,
      pinnable: false,
      headerName: 'Date Closed',
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row) => {
        return formatDayjsDateTime(row?.timeCompleted)
      },
      renderCell: (params: GridRenderCellParams) => (
        <DateCell date={params?.value} params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'seoTier',
      aggregable: false,
      pinnable: false,
      headerName: 'Tier',
      width: 75,
      // flex: 0.5,
      valueGetter: (_, row) => {
        return row?.seoTier?.label
      },
      renderCell: params => <SeoTierCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'dependenciesDueDate',
      aggregable: false,
      pinnable: false,
      headerName: 'Dependencies Due',
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row) => {
        return formatDateString(row?.dependenciesDueDate)
      },
      renderCell: (params: GridRenderCellParams) => (
        <DateCell date={params?.value} params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'estLaunchDate',
      aggregable: false,
      pinnable: false,
      headerName: 'Estimated Launch Date',
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row) => {
        return formatDateString(row?.estLaunchDate)
      },
      renderCell: (params: GridRenderCellParams) => (
        <DateCell date={params?.value} params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'dateLaunched',
      aggregable: false,
      pinnable: false,
      headerName: 'Date Launched',
      width: 100,
      // flex: 0.65,
      editable: true,
      valueGetter: (_, row) => row?.dateLaunched ?? null,
      renderCell: (params: GridRenderCellParams) => {
        const handleStop = () => {
          return
        }
        return !params?.row?.isParent ? (
          <DateCell date={params?.value} params={params} densityStyles={densityStyles} onClick={handleClickEditStart} />
        ) : (
          <InactiveCell densityStyles={densityStyles} onClick={handleStop} />
        )
      },
      renderEditCell: (params: GridRenderEditCellParams<any, string | null>) => {
        const handleChange = async (newValue: Dayjs | null) => {
          await params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue ? newValue.toISOString() : null, // Save as ISO string
          })
          params.api.stopCellEditMode({ id: params.id, field: params.field })
        }
        const handleStop = () => {
          params.api.stopCellEditMode({
            id: params.id,
            field: params.field,
          })
        }

        return !params?.row?.isParent ? (
          <EditDatePicker params={params} onChange={handleChange} />
        ) : (
          <InactiveCell densityStyles={densityStyles} onClick={handleStop} />
        )
      },
    },
    {
      field: 'startDate',
      aggregable: false,
      headerName: 'Start Date',
      width: 100,
      // flex: 0.65,
      editable: true,
      valueFormatter: (_, row) => {
        return formatDateString(row?.startDate) ?? null
      },
      valueGetter: (_, row) => {
        return row?.startDate ?? null
      },
      // filterOperators: [...getGridDateOperators()],
      renderCell: (params: GridRenderCellParams) => {
        const formattedDate = params?.value ? formatDateString(params?.value) : null
        const handleStop = () => {
          return
        }
        return !params?.row?.isParent ? (
          <DateCell date={formattedDate} params={params} densityStyles={densityStyles} onClick={handleClickEditStart} />
        ) : (
          <InactiveCell densityStyles={densityStyles} onClick={handleStop} />
        )
      },
      renderEditCell: (params: GridRenderEditCellParams<any, string | null>) => {
        const handleChange = async (newValue: Dayjs | null) => {
          await params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue ? newValue.toISOString() : null, // Save as ISO string
          })
          params.api.stopCellEditMode({ id: params.id, field: params.field })
        }
        const handleStop = () => {
          params.api.stopCellEditMode({
            id: params.id,
            field: params.field,
          })
        }

        return !params?.row?.isParent ? (
          <EditDatePicker params={params} onChange={handleChange} />
        ) : (
          <InactiveCell densityStyles={densityStyles} onClick={handleStop} />
        )
      },
    },
    // {
    //   field: 'startDate',
    //   aggregable: false,
    //   editable: true,
    //   headerName: 'Start Date',
    // flex: 0.3,
    //   headerAlign: 'center',
    //   valueGetter: (_value, row) => {
    //     return row?.startDate || null
    //   },
    //   filterOperators: [...getGridStringOperators(), customNotContainsOperator],
    //   sortComparator: customDateSortOperator,
    //   renderCell: (params: GridRenderCellParams) => <DateTimeCell params={params} densityStyles={densityStyles} />,
    //   renderEditCell: (params: GridRenderEditCellParams<any, string | null>) => {
    //     const handleChange = async (newValue: Dayjs | null) => {
    //       await params.api.setEditCellValue({
    //         id: params.id,
    //         field: params.field,
    //         value: newValue ? newValue.toISOString() : null, // Save as ISO string
    //       })
    //       params.api.stopCellEditMode({ id: params.id, field: params.field })
    //     }
    //     const handleStop = () => {
    //       params.api.stopCellEditMode({
    //         id: params.id,
    //         field: params.field,
    //       })
    //     }

    //     return !params?.row?.isParent ? (
    //       <EditDatePicker params={params} onChange={handleChange} />
    //     ) : (
    //       <InactiveCell densityStyles={densityStyles} onClick={handleStop} />
    //     )
    //   },
    // },
    {
      field: 'dateRange',
      headerName: 'Date Range',
      headerClassName: 'date-range-column',
      valueGetter: (_, row) => {
        const dateRange = row?.SK?.startsWith('PRODUCT_') ? [row?.startDate, row?.endDate] : row?.dateRange
        // console.log('dateRange', dateRange)
        return dateRange ?? []
      },
      filterOperators: customMonthRangeFilterOperators,
      renderCell: (params: GridRenderCellParams<any, any>) => {
        const [start, end] = params.value || []
        if (!start || !end) return ''
        return `${dayjs(start).format('MMM D, YYYY')} - ${dayjs(end).format('MMM D, YYYY')}`
      },
    },

    {
      field: 'endDate',
      aggregable: false,
      headerName: 'Due Date',
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row) => {
        return formatDateString(row?.endDate)
      },
      renderCell: (params: GridRenderCellParams) => (
        <DateCell date={params?.value} params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'contractedBy',
      aggregable: false,
      pinnable: false,
      headerName: 'Contracted By',
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row) => {
        return row?.contractedByOrg?.name ?? ''
      },
      renderCell: params => {
        return <ContractedByCell params={params} densityStyles={densityStyles} />
      },
    },
    {
      field: 'totalMediaSpend',
      headerName: 'Total Spend',
      type: 'number',
      aggregable: false, // Using pre-computed totals
      valueGetter: (_, row): number => {
        // For parent rows, use pre-computed total; for product rows, use product's mediaSpend
        if (row?.isParent) return row?.totalMediaSpend ?? 0
        return parseFloat(row?.mediaSpend) || 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'monthlyMediaSpend',
      headerName: 'Mo. Spend',
      aggregable: false, // Using pre-computed totals
      width: 100,
      type: 'number',
      valueGetter: (_, row): number => {
        // For parent rows, use pre-computed total; for product rows, use product's mediaSpend
        if (row?.isParent) return row?.totalMediaSpend ?? 0
        return parseFloat(row?.mediaSpend) || 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'totalContractAmount',
      headerName: 'Total',
      type: 'number',
      width: 125,
      aggregable: false, // Using pre-computed totals
      valueGetter: (_, row): number => {
        // For parent rows, use pre-computed total; for product rows, use product's price
        if (row?.isParent) return row?.totalContractAmount ?? 0
        return parseFloat(row?.price) || 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    // {
    //   field: 'monthlyContractAmount',
    //   headerName: 'Mo. Total',
    //   type: 'number',
    //   aggregable: true,
    //   valueGetter: (_, row) => row?.monthlyContractAmount ?? undefined,
    //   renderCell: params => <MonthlyTotalAggregateCell params={params} densityStyles={densityStyles} />,
    // },
    {
      field: 'msaMonthlyTotal',
      headerName: 'MSA Mo. Total',
      aggregable: true,
      type: 'number',
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row): number => {
        return row?.contractType && ['MSA', 'RMSA', 'MSA - Housing', 'RMSA - Housing']?.includes(row.contractType)
          ? parseFloat(row?.price)
          : 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'ioMonthlyTotal',
      type: 'number',
      aggregable: true,
      headerName: 'IO Mo. Total',
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row): number => {
        return row?.contractType && ['IO']?.includes(row.contractType) ? parseFloat(row?.price) : 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'csaMonthlyTotal',
      type: 'number',
      aggregable: true,
      headerName: 'CSA Mo. Total',
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row): number => {
        const value =
          row?.contractType && ['CSA', 'RCSA']?.includes(row.contractType)
            ? parseFloat(row?.price) / row?.numberOfPayments
            : 0
        return value ?? 0
        // return row?.contractType && ['CSA', 'RCSA']?.includes(row.contractType) ? parseFloat(row?.price) : 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'totalMonthlyFee',
      type: 'number',
      aggregable: true,
      headerName: 'Imp. Mo. Total',
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row): number => {
        return row?.totalMonthlyFee
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'month',
      headerName: 'Month',
      width: 0,
      aggregable: false,
      pinnable: false,
      filterable: true,
      type: 'string',
      valueGetter: (_, row) => {
        const taskMonths = !row?.isParent && row?.timeCreated ? [dayjs(row.timeCreated ?? null).format('YYYY-MM')] : []
        return row?.isParent ? Object.keys(row?.monthlyBreakdown ?? []) : taskMonths
      },
      filterOperators: customMonthFilterOperators,
    },
    // {
    //   field: 'allMonthlyContractAmount',
    //   headerName: 'All Mo. Contract Amount',
    //   width: 100,
    // flex: 0.4,
    //   valueGetter: (_, row): number => {
    //     return row?.allMonthlyContractAmount
    //   },
    //   renderCell: (params: GridRenderCellParams) => <CurrencyCell params={params} densityStyles={densityStyles} />,
    // },
    {
      field: 'lastModified',
      headerName: 'Modified',
      aggregable: false,
      pinnable: false,
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row) => {
        return formatDayjsModifiedTime(row?.lastModified)
      },
      // renderCell: (params: GridRenderCellParams) => (
      //   <DateCell date={params?.value} params={params} densityStyles={densityStyles} />
      // ),
    },
    {
      field: 'status',
      aggregable: false,
      pinnable: false,
      headerName: 'Client Status',
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row) => {
        return row?.status
      },
      // renderCell: (params: GridRenderCellParams) => <StatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'website',
      aggregable: false,
      pinnable: false,
      headerName: 'Website',
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row) => {
        return row?.website
      },
      // renderCell: (params: GridRenderCellParams) => <WebsiteCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'implementationFee',
      type: 'number',
      aggregable: true,
      headerName: 'Impl. Fee',
      width: 100,
      // flex: 0.4,
      valueGetter: (_, row) => {
        return row?.implementationFee
      },
      // renderCell: (params: GridRenderCellParams) => <CurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'total',
      headerName: 'Total',
      type: 'number',
      aggregable: true,
      // flex: 0.4,
      valueGetter: (_, row) => {
        return row?.total
      },
      // renderCell: (params: GridRenderCellParams) => <CurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'grossProfit',
      headerName: 'Gross Profit',
      type: 'number',
      aggregable: false, // Using pre-computed totals
      valueFormatter: currencyValueFormatter,
      valueGetter: (_, row): number => {
        // For parent rows, use pre-computed total; for product rows, use product's grossProfit
        if (row?.isParent) return row?.grossProfit ?? 0
        return parseFloat(row?.grossProfit) || 0
      },
      renderCell: params => <AggregateCurrencyCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'AM',
      headerName: 'AM',
      width: 55,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['AM']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'BDE',
      headerName: 'BDE',
      width: 55,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['BDE']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'DMS',
      headerName: 'DMS',
      width: 55,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['DMS']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'MIA',
      headerName: 'MIA',
      width: 55,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['MIA']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'PM',
      headerName: 'PM',
      width: 55,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['PM']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'QA',
      headerName: 'QA',
      width: 55,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['QA']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'SEO',
      headerName: 'SEO',
      width: 55,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['SEO']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'other',
      headerName: 'other',
      width: 85,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['other']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    // {
    //   field: 'team_details',
    //   headerName: 'Team',
    //   type: 'custom',
    //   aggregable: false,
    //   pinnable: false,
    //   // flex: 0.4,
    //   valueGetter: (_, row) => {
    //     return row?.team_details?.map((user: User) => user.name).join(', ')
    //   },
    //   renderCell: (params: GridRenderCellParams) => {
    //     return <TeamDetailsCell params={params} densityStyles={densityStyles} />
    //   },
    // },
    // ...renderChannels(),
  ]
}
